import React, {
	Context,
	useCallback,
	useState,
	useContext,
	useEffect,
	useRef,
} from 'react';

export interface AVDataInterface {
	id: string;
	audio?: boolean;
	video?: boolean;
}

type AVDataSet = Record<AVDataInterface['id'], AVDataInterface>
export interface AVHookInterface {
	setAV: (newData: AVDataInterface) => void;
	getAV: (id: string) => AVDataInterface;
	getAllAV: () => AVDataSet;
	removeAV: (id: string) => void;
}

export const baseState = { id: 'base', audio: true, video: true };

// what you call to gain access to the useAVContext hook from components
export const AVContext = React.createContext({
	setAV: () => undefined,
	getAV: () => ({}),
	getAllAV: () => ({}),
	removeAV: () => undefined,
}) as unknown as Context<AVHookInterface>;

// what you pass into the React.provider component as the value
export function useAudioVideo(): AVHookInterface {
	const [avData, setAVData] = useState<AVDataSet>({});
	const isFirstLoad = useRef(true);
	const getAVFromLocalStorage = (): AVDataSet => {
		const avDataLocal = localStorage.getItem('avData');
		return avDataLocal ? JSON.parse(avDataLocal) : {};
	};

	const setAVToLocalStorage = (newData: AVDataSet): void => {
		const currentLocalStorage = getAVFromLocalStorage();
		localStorage.setItem('avData', JSON.stringify({ ...currentLocalStorage, ...newData }));
	};

	const setAV = (newData: AVDataInterface): void => {
		if (isFirstLoad.current) {
			isFirstLoad.current = false;

			const avLocalStorage = getAVFromLocalStorage();
			if (avLocalStorage[newData.id]) {
				setAVData({ ...avLocalStorage });
				return;
			}
		}
		setAVData((prev: AVDataSet) => {
			if (newData.id === 'local') {
				setAVToLocalStorage({ [newData.id]: { ...newData } });
			}
			return (
				{ ...prev, [newData.id]: { ...newData } }
			);
		});
	};

	const removeAV = (id: string): void => {
		setAVData((prev: AVDataSet) => {
			const newSet = prev;
			delete newSet[id];
			return newSet;
		});
	};

	const getAV = useCallback(
		(id: string): AVDataInterface => {
			const avLocalStorage = getAVFromLocalStorage();
			if (avLocalStorage[id]) {
				return avLocalStorage[id];
			}
			return ({ ...(avData[id] || baseState), id });
		},
		[avData],
	);

	const getAllAV = useCallback((): AVDataSet => avData, [avData]);

	useEffect(() => {
		setAV({ ...baseState, id: 'local' });
	}, []);

	return {
		setAV,
		getAV,
		getAllAV,
		removeAV,
	};
}

export const useGetAV = (id: string = '') => {
	const { getAV } = useContext(AVContext);
	return getAV(id);
};

export const AVProvider = (
	props: any,
): React.ReactElement<any, React.Provider<AVHookInterface>> => {
	const avContextValue = useAudioVideo();
	return <AVContext.Provider value={avContextValue}>
		{props.children}
	</AVContext.Provider>;
};
